import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarOff, Loader2, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/agency-holidays`;
const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

export default function AgencyHolidaysPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ date: '', name: '' });
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'system_admin';

  const load = useCallback(() => {
    axios.get(API).then((r) => setItems(r.data.holidays)).catch(() => toast.error('Failed to load agency holidays'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.date || !form.name.trim()) return toast.error('Pick a date and enter a holiday name');
    setSaving(true);
    try { await axios.post(API, form); toast.success('Holiday added'); setForm({ date: '', name: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not add the holiday'); }
    finally { setSaving(false); }
  };
  const remove = async (h) => {
    try { await axios.delete(`${API}/${h.id}`); toast.success('Holiday removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove the holiday'); }
  };

  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-6 max-w-3xl" data-testid="agency-holidays-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarOff className="h-6 w-6" />Agency Holidays</h1>
        <p className="text-muted-foreground text-sm">Automatic texts (appointment reminders, HELPConnect setup reminders and inactivity messages) only go out on working days: never on Saturdays, Sundays or these dates.</p>
      </div>
      {isAdmin && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Add a holiday</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2">
            <Input type="date" className="w-44" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} aria-label="Holiday date" data-testid="holiday-date-input" />
            <Input className="flex-1 min-w-[200px]" placeholder="Holiday name, e.g. Thanksgiving" maxLength={120} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="holiday-name-input" />
            <Button onClick={add} disabled={saving} data-testid="add-holiday-btn">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add</Button>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Holidays</CardTitle>
          <CardDescription>Reminders due on a holiday or weekend are sent on the last working day before.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {!items ? <Loader2 className="h-5 w-5 animate-spin m-6" /> : items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground" data-testid="holidays-empty">No agency holidays yet.</p>
          ) : (
            <ul className="divide-y border-t">
              {items.map((h) => (
                <li key={h.id} className={`flex items-center justify-between gap-3 px-6 py-3 ${h.date < today ? 'opacity-60' : ''}`} data-testid={`holiday-row-${h.id}`}>
                  <div><div className="font-medium">{h.name}</div><div className="text-sm text-muted-foreground">{fmt(h.date)}</div></div>
                  {isAdmin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(h)} aria-label={`Remove ${h.name}`} data-testid={`delete-holiday-${h.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
